
import {
  Vue, Component, Mixins, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import UpdateButton from '@/components/UpdateButton.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import AttachmentWithStatus from '@/components/AttachmentWithStatus.vue';
import Editor from '@/components/Editor.vue';
import AssignmentEntryItem from '@/components/AssignmentEntryItem.vue';
import OperationAssignmentsDialog from '@/components/OperationAssignmentsDialog.vue';
import { BoxStatusType, User, AssignmentEntry } from '@/types';
import {
  getLeadCustomValue,
  getLeadCustomValueText, getLeadCustomValueDate,
} from '@/helpers';

import UtilsMixin from '@/mixins/UtilsMixin.vue';
import EditConnectionDialogMixin from '@/mixins/EditConnectionDialogMixin.vue';

moment.locale('it');

@Component({
  components: {
    UpdateButton,
    DatePickerDialog,
    AttachmentWithStatus,
    Editor,
    AssignmentEntryItem,
    OperationAssignmentsDialog,
  },
})
export default class EditEneaPracticeDialog extends Mixins(EditConnectionDialogMixin, UtilsMixin) {
  eneaPracticeDate: Date | null = null
  eneaElettricoNotes: string | null = null

  praticaEnea: BoxStatusType = 'todo'

  // ENEA assignment
  eneaAssigneeId: number | null = null
  eneaAssigned: string | null = null
  eneaAssignedDate: Date | null = null
  eneaAssignees: string | null = null
  editEneaAssignmentDialog = false

  // Aggiorna i campi con gli eventuali nuovi valori della Connection/Lead selezionata
  refresh() {
    console.log(`${this.constructor.name}: refresh()`); // eslint-disable-line no-console

    // Aggiorna i campi con i valori della Connection
    this.eneaPracticeDate = this.connection?.eneaPracticeDate ?? null;

    this.praticaEnea = (this.connection?.praticaEnea ?? 'todo') as BoxStatusType;

    if (this.currentElettricaFiglia) {
      this.eneaElettricoNotes = getLeadCustomValueText(this.currentElettricaFiglia, 'eneaElettricoNotes') ?? null;
    }

    this.refreshEneaAssignment();
    const assignmentTypes = this.$store.getters.getAssignmentTypes || [];
    if (assignmentTypes.length === 0) {
      this.$store.dispatch('fetchAssignmentTypes');
    }

    // Richiama il metodo del mixin
    return this.mixinRefresh();
  }

  onFieldUpdated(field: string, value: unknown = undefined) {
    console.log(`${this.constructor.name}: onFieldUpdated(${field})`); // eslint-disable-line no-console

    if (value !== undefined) this[field] = value;

    const update = {} as { [key: string]: unknown };

    update[field] = this[field] ?? '';

    const form = this.getForm();

    this.eneaPracticeDate = moment(moment(), 'YYYY-MM-DD').toDate();
    update.eneaPracticeDate = this.eneaPracticeDate;

    if (form) form.validate();

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });

    // Richiama il metodo del mixin
    return this.mixinOnFieldUpdated(field, value);
  }

  onNotesUpdated(field: string) {
    return this.mixinOnFieldUpdated(field);
  }

  getForm(): (Vue & { validate: () => boolean; reset: () => boolean }) | undefined {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  get users(): User[] {
    return this.$store.getters.users;
  }

  get eneaAssigneeName(): string | null {
    if (!this.eneaAssigneeId) return null;
    const user = this.users.find((u) => u.id === this.eneaAssigneeId);
    return user?.name ?? null;
  }

  get eneaEntry(): AssignmentEntry {
    const config = this.$store.getters.getAssignmentType('enea');
    return {
      type: 'enea',
      label: config?.label || 'Assegnatario ENEA/GSE/Altro',
      icon: config?.icon || 'mdi-certificate',
      dateTooltip: 'Data assegnazione ENEA/GSE/Altro',
      permissionShow: 'eneaAssignment.show',
      permissionEdit: 'eneaAssignment.edit',
      multipleAssignees: false,
      assignees: [],
      assigneeId: this.eneaAssigneeId,
      assigneeName: this.eneaAssigneeName,
      assigned: this.eneaAssigned,
      assignedDate: this.eneaAssignedDate,
    };
  }

  get eneaAssignmentOperation(): Record<string, unknown> | null {
    const child = this.currentElettricaFiglia;
    if (!child) return null;
    return {
      children: {
        [child.id]: {
          type: 'elettrica-figlia',
          eneaAssigneeId: this.eneaAssigneeId,
          eneaAssigned: this.eneaAssigned,
          eneaAssignedDate: this.eneaAssignedDate,
          eneaAssignees: this.eneaAssignees,
          firstTaskId: child.Tasks?.[0]?.id || null,
        },
      },
    };
  }

  onEditEneaAssignment() {
    if (!this.hasPermission('eneaAssignment.edit')) return;
    if (this.fieldsDisabled) return;
    this.editEneaAssignmentDialog = true;
  }

  @Watch('currentElettricaFiglia')
  onElettricaFigliaChange() {
    this.eneaElettricoNotes = getLeadCustomValueText(this.currentElettricaFiglia, 'eneaElettricoNotes') ?? null;
    this.refreshEneaAssignment();
  }

  @Watch('editEneaAssignmentDialog')
  onEditEneaAssignmentDialogChange(val: boolean) {
    if (!val) {
      // Dopo la chiusura del dialog, ricarica i dati
      this.refreshEneaAssignment();
    }
  }

  refreshEneaAssignment() {
    const child = this.currentElettricaFiglia;
    if (!child) return;
    const childAny = child as unknown as Record<string, unknown>;
    const eneaFromTopLevel = 'eneaAssigneeId' in childAny || 'eneaAssigned' in childAny;
    const eneaAssigneeCV = getLeadCustomValue(child, 'eneaAssignee');
    this.eneaAssigneeId = eneaFromTopLevel && childAny.eneaAssigneeId != null
      ? Number(childAny.eneaAssigneeId)
      : (eneaAssigneeCV?.userValueId ?? null);
    this.eneaAssigned = (eneaFromTopLevel && childAny.eneaAssigned != null
      ? childAny.eneaAssigned
      : (getLeadCustomValueText(child, 'eneaAssigned') ?? 'todo')) as string;
    this.eneaAssignedDate = eneaFromTopLevel && childAny.eneaAssignedDate != null
      ? (childAny.eneaAssignedDate as Date)
      : getLeadCustomValueDate(child, 'eneaAssignedDate');
    this.eneaAssignees = getLeadCustomValueText(child, 'eneaAssignees') ?? '[]';
  }
}
